import React from 'react';
import PropTypes from 'prop-types';
import { Button, Box, useTheme, styled } from '@mui/material';
import { Visibility, Favorite, Share, Download, Cookie, FavoriteBorder } from '@mui/icons-material';

const StatButtonContainer = styled(Button)(({ theme }) => ({
  position: 'relative',
  minWidth: 'fit-content',
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.action.hover
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: theme.spacing(1.5),
  }
}));

const CountBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  minWidth: 24,
  height: 24,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  fontWeight: 600,
  boxShadow: theme.shadows[1],
  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    top: 0,
    right: 0,
    marginLeft: 'auto',
    borderRadius: '8px'
  }
}));

const StatButton = ({ icon, label, count, onClick, color }) => {
  const theme = useTheme();

  return (
    <StatButtonContainer
      variant="text"
      startIcon={icon}
      onClick={onClick}
      sx={{
        color: color || theme.palette.text.primary,
        '& .MuiButton-startIcon': {
          marginRight: theme.spacing(1),
          [theme.breakpoints.down('sm')]: {
            marginRight: theme.spacing(1.5)
          }
        }
      }}
    >
      <Box component="span" sx={{ 
        fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
        fontWeight: 500 
      }}>
        {label}
      </Box>
      <CountBadge>{count}</CountBadge>
    </StatButtonContainer>
  );
};

const RecipeStats = ({
  viewsCount,
  likedCount,
  downloadedCount,
  cookedCount,
  handleLike,
  handleShare,
  isLiked
}) => {
  const theme = useTheme();

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { 
        xs: 'repeat(2, 1fr)', 
        sm: 'repeat(4, 1fr)', 
        md: 'repeat(5, 1fr)' 
      },
      gap: 2,
      py: 3,
      px: { xs: 1, sm: 2 },
      borderTop: `1px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius * 2,
      boxShadow: theme.shadows[1],
      [theme.breakpoints.down('sm')]: {
        gap: 1,
        borderRadius: theme.shape.borderRadius
      }
    }}>
      <StatButton
        icon={<Visibility fontSize="small" />}
        label="Views"
        count={viewsCount}
      />
      
      <StatButton
        icon={isLiked ? 
          <Favorite fontSize="small" color="error" /> : 
          <FavoriteBorder fontSize="small" />
        }
        label={isLiked ? 'Liked' : 'Like'}
        count={likedCount}
        onClick={handleLike}
        color={isLiked ? theme.palette.error.main : undefined}
      />
      
      <StatButton
        icon={<Share fontSize="small" />}
        label="Share"
        count={0}
        onClick={handleShare}
      />
      
      <StatButton
        icon={<Download fontSize="small" />}
        label="Downloads"
        count={downloadedCount}
      />
      
      <StatButton
        icon={<Cookie fontSize="small" />}
        label="Cooked"
        count={cookedCount}
      />
    </Box>
  );
};

StatButton.propTypes = {
    icon: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    onClick: PropTypes.func,
    color: PropTypes.string
  };
  
  StatButton.defaultProps = {
    onClick: () => {},
    color: 'text.primary'
  };
RecipeStats.propTypes = {
  viewsCount: PropTypes.number,
  likedCount: PropTypes.number,
  downloadedCount: PropTypes.number,
  cookedCount: PropTypes.number,
  handleLike: PropTypes.func,
  handleShare: PropTypes.func,
  isLiked: PropTypes.bool
};

RecipeStats.defaultProps = {
  viewsCount: 0,
  likedCount: 0,
  downloadedCount: 0,
  cookedCount: 0,
  handleLike: () => {},
  handleShare: () => {},
  isLiked: false
};

export default RecipeStats;
